import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskTypeStatus, type TaskCardType, type UserTypeShort } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { UserNameItem } from 'modules/user';
import TaskUserRoleEditForm from '../../shared/TaskUserRoleEditForm';
import type { TaskUserRole } from '../../shared/helpers/taskUsersHelpers';

interface TaskCardUsersTabProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
}

const TaskCardUsersTab = ({ task, onTaskUpdated }: TaskCardUsersTabProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const [editingRole, setEditingRole] = useState<TaskUserRole | null>(null);

    const userId = Number(auth?.user?.ID || 0);
    const status = Number(task.taskStatus);
    const isOwner = userId > 0 && userId === Number(task.userId);
    const isController = userId > 0 && userId === Number(task.taskController);
    const isDeleted = Boolean(Number(task.taskDeleted));
    const canEditUsers =
        !isDeleted &&
        (isOwner || isController) &&
        (status === TaskTypeStatus.NEW || status === TaskTypeStatus.INWORK);

    useEffect(() => {
        if (!canEditUsers) setEditingRole(null);
    }, [canEditUsers]);

    const renderChangeButton = (role: TaskUserRole) => {
        if (!canEditUsers) return null;
        return (
            <div className="text-end flex-shrink-0">
                <button
                    type="button"
                    className="btn btn-link p-0 align-baseline fs-12"
                    onClick={() => setEditingRole(role)}
                >
                    <i className="bi bi-pencil me-1 fs-14"></i>
                    {t('common:buttons.change')}
                </button>
            </div>
        );
    };

    const renderUserPanel = (role: TaskUserRole | null, user?: UserTypeShort | null) => {
        if (role && editingRole === role) {
            return (
                <div className="task-card-panel">
                    <TaskUserRoleEditForm
                        task={task}
                        role={role}
                        onCancel={() => setEditingRole(null)}
                        onSaved={(next) => {
                            setEditingRole(null);
                            onTaskUpdated(next);
                        }}
                    />
                </div>
            );
        }

        return (
            <div className="task-card-panel">
                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-end justify-content-between gap-2">
                    <div className="flex-grow-1 min-w-0">
                        {user?.ID ? (
                            <UserNameItem user={user} />
                        ) : (
                            <span className="text-muted">—</span>
                        )}
                    </div>
                    {role ? renderChangeButton(role) : null}
                </div>
            </div>
        );
    };

    return (
        <>
            <section className="task-card-section mb-3">
                <div className="task-card-section__label">{t('labelAuthor')}</div>
                {renderUserPanel(null, task.authorUser)}
            </section>

            <section className="task-card-section mb-3">
                <div className="task-card-section__label">{t('labelExecutor')}</div>
                {renderUserPanel('executor', task.executorUser)}
            </section>

            <section className="task-card-section mb-3">
                <div className="task-card-section__label">{t('labelController')}</div>
                {renderUserPanel('controller', task.controllerUser)}
            </section>
        </>
    );
};

export default TaskCardUsersTab;
